import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Generation Loading Overlay Component
 * Displays a professional loading screen with progress bar and step indicators
 * 
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the overlay is visible
 * @param {number} props.duration - Total duration in milliseconds (default: 240000 = 4 minutes)
 * @param {Array<string>} props.steps - Array of step strings to display
 * @param {number} props.stepDuration - Duration per step in milliseconds (default: 30000 = 30 seconds)
 */
export default function GenerationLoadingOverlay({
  isVisible = false,
  duration = 240000, // 4 minutes
  steps = [],
  stepDuration = 30000, // 30 seconds per step
}) {
  const [progress, setProgress] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  useEffect(() => {
    if (!isVisible) {
      setProgress(0);
      setCurrentStepIndex(0);
      setCompletedSteps(new Set());
      return;
    }

    const startTime = Date.now();
    const totalSteps = steps.length;
    const stepInterval = stepDuration;

    // Progress bar update interval (smooth animation)
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      // Update current step based on elapsed time
      const currentStep = Math.min(
        Math.floor(elapsed / stepInterval),
        totalSteps - 1
      );
      
      setCurrentStepIndex(prevStep => {
        if (currentStep !== prevStep) {
          // Mark previous steps as completed
          setCompletedSteps(prev => {
            const newSet = new Set(prev);
            for (let i = 0; i < currentStep; i++) {
              newSet.add(i);
            }
            return newSet;
          });
          return currentStep;
        }
        return prevStep;
      });
    }, 100); // Update every 100ms for smooth animation

    return () => {
      clearInterval(progressInterval);
    };
  }, [isVisible, duration, stepDuration, steps.length]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Generating Your Collateral
          </h2>
          <p className="text-gray-600">
            This may take a few minutes. Please don't close this window.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-semibold text-blue-600">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="h-full w-full bg-gradient-to-r from-blue-400 to-purple-500 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Steps List */}
        {steps.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Generation Steps
            </h3>
            <div className="space-y-3">
              {steps.map((step, index) => {
                const isCompleted = completedSteps.has(index);
                const isCurrent = index === currentStepIndex && !isCompleted;
                const isPending = index > currentStepIndex;

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                      isCurrent
                        ? 'bg-blue-50 border-2 border-blue-200'
                        : isCompleted
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-gray-50 border border-gray-200 opacity-60'
                    }`}
                  >
                    {/* Step Icon */}
                    <div className="flex-shrink-0">
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : isCurrent ? (
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                      )}
                    </div>

                    {/* Step Text */}
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${
                          isCurrent
                            ? 'text-blue-900'
                            : isCompleted
                            ? 'text-green-900'
                            : 'text-gray-500'
                        }`}
                      >
                        {step}
                      </p>
                    </div>

                    {/* Step Number */}
                    <div className="flex-shrink-0">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          isCurrent
                            ? 'bg-blue-200 text-blue-800'
                            : isCompleted
                            ? 'bg-green-200 text-green-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {index + 1}/{steps.length}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-center text-gray-500">
            Parrot is crafting your personalized collateral. This process ensures
            high-quality, tailored content for your prospect.
          </p>
        </div>
      </div>
    </div>
  );
}

